import { useState, useEffect, useRef, useMemo } from 'react';
import { BenefitDto, BenefitFiltersDto } from '@/services/platform/api/model';
import { useBenefitsAutocomplete, useBenefitsSearch } from '@/services/platform/data/profile/benefits';
import { useLocaleStore } from '@/lib/core/stores';
import { Icon } from '@/components/ui/icon';
import { Button } from '@/components/ui/button';
import { SpinLoading } from '@/components/shared/loadings/spin';
import { BenefitsFilterSheet } from '@/components/shared/sheets/benefits-filter';
import { useClientTranslation } from '@/lib/core';

const HISTORY_KEY = 'benefits-search-history';
const MAX_HISTORY = 5;

function readHistory(): string[] {
  try {
    return JSON.parse(localStorage.getItem(HISTORY_KEY) ?? '[]');
  } catch {
    return [];
  }
}

function saveToHistory(query: string) {
  const current = readHistory().filter(q => q !== query);
  localStorage.setItem(HISTORY_KEY, JSON.stringify([query, ...current].slice(0, MAX_HISTORY)));
}

type SearchState = 'idle' | 'typing' | 'results';

interface BenefitsSearchOverlayProps {
  open: boolean;
  onClose: () => void;
  filters: BenefitFiltersDto;
}

export function BenefitsSearchOverlay({ open, onClose, filters }: BenefitsSearchOverlayProps) {
  const translation = useClientTranslation();

  const [query, setQuery] = useState('');
  const [debouncedQuery, setDebouncedQuery] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [searchState, setSearchState] = useState<SearchState>('idle');
  const [suggestions, setSuggestions] = useState<string[]>([]);
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [activeCategories, setActiveCategories] = useState<string[]>([]);
  const [activeLocations, setActiveLocations] = useState<string[]>([]);

  const inputRef = useRef<HTMLInputElement>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const locale = useLocaleStore(state => state.locale);

  const { data: autocompleteData, isLoading: isAutocompleteLoading } = useBenefitsAutocomplete(
    debouncedQuery,
    locale
  );
  const autocompleteSuggestions: string[] =
    autocompleteData?.status === 200 ? (autocompleteData.data ?? []) : [];

  const { data: searchData, isLoading: isSearchLoading } = useBenefitsSearch(searchQuery, locale);
  const serverResults: BenefitDto[] = searchData?.status === 200 ? (searchData.data?.benefits ?? []) : [];

  const displayedResults = useMemo(() => {
    let filtered = serverResults;
    if (activeCategories.length > 0) {
      filtered = filtered.filter(b => b.categories?.some(c => activeCategories.includes(c.id ?? '')));
    }
    if (activeLocations.length > 0) {
      filtered = filtered.filter(b => b.locations?.some(l => activeLocations.includes(l.id ?? '')));
    }
    return filtered;
  }, [serverResults, activeCategories, activeLocations]);

  useEffect(() => {
    if (open) {
      setSuggestions(readHistory());
      setQuery('');
      setDebouncedQuery('');
      setSearchQuery('');
      setSearchState('idle');
      setActiveCategories([]);
      setActiveLocations([]);
      setTimeout(() => inputRef.current?.focus(), 100);
    }
  }, [open]);

  function handleQueryChange(value: string) {
    setQuery(value);
    if (value.length < 2) {
      setSearchState('idle');
      setDebouncedQuery('');
      return;
    }
    setSearchState('typing');
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      setDebouncedQuery(value);
    }, 300);
  }

  function handleSubmit() {
    if (query.length < 2) return;
    saveToHistory(query);
    setSuggestions(readHistory());
    setActiveCategories([]);
    setActiveLocations([]);
    setSearchQuery(query);
    setSearchState('results');
    inputRef.current?.blur();
  }

  function handleSuggestion(suggestion: string) {
    setQuery(suggestion);
    setActiveCategories([]);
    setActiveLocations([]);
    setSearchQuery(suggestion);
    setSearchState('results');
    saveToHistory(suggestion);
    setSuggestions(readHistory());
  }

  function handleSearchAgain() {
    setQuery('');
    setDebouncedQuery('');
    setSearchQuery('');
    setSearchState('idle');
    setActiveCategories([]);
    setActiveLocations([]);
    setTimeout(() => inputRef.current?.focus(), 50);
  }

  function handleApplyFilters(categories: string[], locations: string[]) {
    setActiveCategories(categories);
    setActiveLocations(locations);
  }

  function handleClearFilters() {
    setActiveCategories([]);
    setActiveLocations([]);
  }

  const activeFilterCount = activeCategories.length + activeLocations.length;

  if (!open) return null;

  return (
    <>
      <div
        className="pt-safe-area fixed inset-0 z-60 flex flex-col bg-white"
        role="dialog"
        aria-modal="true"
        aria-label={translation('App.Labels.BenefitSearchHint')}
      >
        {/* Top bar */}
        <div
          className="flex shrink-0 items-center gap-2 px-2"
          style={{ paddingTop: 'max(1rem, env(safe-area-inset-top))' }}
        >
          <button
            type="button"
            aria-label={translation('App.Labels.Close')}
            className="flex size-11 shrink-0 cursor-pointer items-center justify-center rounded-full"
            onClick={onClose}
          >
            <Icon name="xmark" width={24} height={24} className="text-[#1a1a1a]" />
          </button>

          {searchState === 'results' && query && (
            <p className="font-pp-neue-montreal flex-1 text-[14px] leading-none tracking-[0.5px] text-[#1a1a1a] uppercase">
              {query}
            </p>
          )}
        </div>

        {/* Results count bar (results state only) */}
        {searchState === 'results' && (
          <div className="flex shrink-0 items-center justify-between border-b-[0.5px] border-[#d1d1d1] px-4 py-2">
            <p className="font-pp-neue-montreal text-[12px] leading-7.25 tracking-[0.5px] text-[#848484] uppercase">
              {translation('App.Labels.BenefitResultsCount', { count: displayedResults.length })}
            </p>
            <button
              type="button"
              aria-label={translation('App.Labels.FilterTitle')}
              className="relative flex size-11 items-center justify-center"
              onClick={() => setIsFilterOpen(true)}
            >
              <Icon name="filter-list" width={20} height={20} className="text-[#1a1a1a]" />
              {activeFilterCount > 0 && (
                <span className="absolute top-1 right-1 flex size-4 items-center justify-center rounded-full bg-[#1a1a1a] text-[9px] font-semibold text-white">
                  {activeFilterCount}
                </span>
              )}
            </button>
          </div>
        )}

        {/* Search input */}
        {searchState !== 'results' && (
          <div className="shrink-0 px-4 pt-2">
            <div className="flex items-center border-b border-[#c8c8c8] py-2">
              <input
                ref={inputRef}
                type="search"
                value={query}
                onChange={e => handleQueryChange(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && handleSubmit()}
                placeholder={translation('App.Labels.BenefitSearchPlaceholder')}
                className="font-pp-neue-montreal w-full bg-transparent text-[12px] tracking-[0.5px] text-[#848484] uppercase outline-none placeholder:text-[#848484]"
                aria-label={translation('App.Labels.BenefitSearchPlaceholder')}
              />
            </div>
          </div>
        )}

        {/* Main content */}
        <div className="flex-1 overflow-y-auto px-4">
          {/* Idle state: history + hint */}
          {searchState === 'idle' && (
            <div className="flex flex-col gap-8 pt-8">
              {suggestions.length > 0 && (
                <div className="flex flex-col">
                  <p className="font-pp-neue-montreal py-4 text-[12px] leading-7.25 tracking-[0.5px] text-[#1a1a1a] uppercase">
                    {translation('App.Labels.BenefitSearchSuggestions')}
                  </p>
                  <div className="flex gap-6 overflow-x-auto py-2">
                    {suggestions.map(s => (
                      <button
                        key={s}
                        type="button"
                        onClick={() => handleSuggestion(s)}
                        className="font-pp-neue-montreal shrink-0 text-[14px] tracking-[0.5px] text-[#666]"
                      >
                        {s}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              <div className="flex flex-col items-center gap-2 py-8">
                <div className="flex size-11 items-center justify-center">
                  <Icon name="search" width={20} height={20} className="text-[#1a1a1a]" />
                </div>
                <div className="flex flex-col items-center gap-2 text-center">
                  <p className="font-pp-neue-montreal text-[12px] tracking-[0.5px] text-[#1a1a1a] uppercase">
                    {translation('App.Labels.BenefitSearchHint')}
                  </p>
                  <p className="font-pp-neue-montreal text-[14px] tracking-[0.5px] text-[#666]">
                    {translation('App.Labels.BenefitSearchHintSub')}
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Typing state: server autocomplete suggestions */}
          {searchState === 'typing' && query.length >= 2 && (
            <div className="pt-4">
              {isAutocompleteLoading ? (
                <SpinLoading wrapperClassName="min-h-20" />
              ) : autocompleteSuggestions.length > 0 ? (
                <div className="flex flex-col">
                  {autocompleteSuggestions.map(s => (
                    <button
                      key={s}
                      type="button"
                      onClick={() => handleSuggestion(s)}
                      className="font-pp-neue-montreal py-4 text-left text-[14px] tracking-[0.5px] text-[#1a1a1a]"
                    >
                      <HighlightMatch text={s} query={query} />
                    </button>
                  ))}
                </div>
              ) : (
                <p className="font-pp-neue-montreal pt-8 text-center text-[12px] tracking-[0.5px] text-[#848484] uppercase">
                  {translation('App.Labels.BenefitSearchNoResults')}
                </p>
              )}
            </div>
          )}

          {/* Results state: loading */}
          {searchState === 'results' && isSearchLoading && <SpinLoading wrapperClassName="min-h-40" />}

          {/* Results state: list */}
          {searchState === 'results' && !isSearchLoading && displayedResults.length > 0 && (
            <SearchResultGrouped benefits={displayedResults} />
          )}

          {/* Results state: empty */}
          {searchState === 'results' && !isSearchLoading && displayedResults.length === 0 && (
            <div className="flex flex-1 flex-col items-center justify-between pt-30 pb-12">
              <div className="flex flex-col items-center gap-2 text-center">
                <div className="flex size-11 items-center justify-center">
                  <Icon name="area-search" width={20} height={20} className="text-[#1a1a1a]" />
                </div>
                <div className="flex flex-col gap-2">
                  <p className="font-pp-neue-montreal text-[12px] tracking-[0.5px] text-[#1a1a1a] uppercase">
                    {translation('App.Labels.BenefitSearchNoResults')}
                  </p>
                  <p className="font-pp-neue-montreal text-[14px] tracking-[0.5px] text-[#666]">
                    {translation('App.Labels.BenefitSearchNoResultsSub')}
                  </p>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Search again button pinned at bottom for empty results */}
        {searchState === 'results' && !isSearchLoading && displayedResults.length === 0 && (
          <div className="pb-safe-area shrink-0 px-4 pt-4">
            <Button
              variant="primary-solid"
              palette="dark"
              size="lg"
              className="w-full"
              onClick={handleSearchAgain}
            >
              {translation('App.Labels.BenefitSearchAgain')}
            </Button>
          </div>
        )}
      </div>

      <BenefitsFilterSheet
        open={isFilterOpen}
        onOpenChange={setIsFilterOpen}
        filters={filters}
        activeCategories={activeCategories}
        activeLocations={activeLocations}
        onApply={handleApplyFilters}
        onClear={handleClearFilters}
      />
    </>
  );
}

function HighlightMatch({ text, query }: { text: string; query: string }) {
  if (!query) return <>{text}</>;
  const idx = text.toLowerCase().indexOf(query.toLowerCase());
  if (idx === -1) return <>{text}</>;
  return (
    <>
      {text.slice(0, idx)}
      <span className="font-semibold">{text.slice(idx, idx + query.length)}</span>
      {text.slice(idx + query.length)}
    </>
  );
}

function SearchResultGrouped({ benefits }: { benefits: BenefitDto[] }) {
  const groups: { id: string; name: string; items: BenefitDto[] }[] = [];
  for (const b of benefits) {
    const id = b.group?.id ?? '';
    const existing = groups.find(g => g.id === id);
    if (existing) {
      existing.items.push(b);
    } else {
      groups.push({ id, name: b.group?.name ?? '', items: [b] });
    }
  }

  return (
    <div className="flex flex-col gap-12 pt-6 pb-33">
      {groups.map(group => (
        <div key={group.id} className="flex flex-col gap-8">
          {group.name && (
            <p className="font-pp-neue-montreal text-[12px] leading-7.25 tracking-[0.5px] text-[#1a1a1a] uppercase">
              {group.name}
            </p>
          )}
          <div className="flex flex-col gap-4">
            {group.items.map(benefit => (
              <SearchResultCard key={benefit.id} benefit={benefit} />
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

function SearchResultCard({ benefit }: { benefit: BenefitDto }) {
  return (
    <div className="flex flex-col gap-2 border-b-[0.5px] border-[#dfdfdf] pb-4">
      <p className="font-pp-neue-montreal text-[14px] font-semibold tracking-[0.5px] text-[#1a1a1a]">
        {benefit.title}
      </p>
      {benefit.description && (
        <p className="font-pp-neue-montreal text-[14px] tracking-[0.5px] text-[#666]">
          {benefit.description}
        </p>
      )}
    </div>
  );
}
